import React, { useState, useEffect, createContext } from 'react';
import { useParams, useNavigate, Routes, Route, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Breadcrumbs,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CropsIcon from '@mui/icons-material/Agriculture';
import AnimalsIcon from '@mui/icons-material/Pets';
import InventoryIcon from '@mui/icons-material/Inventory';
import WorkersIcon from '@mui/icons-material/People';
import FinanceIcon from '@mui/icons-material/AttachMoney';
import TasksIcon from '@mui/icons-material/Assignment';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import WeatherIcon from '@mui/icons-material/WbSunny';
import SmartFarmingIcon from '@mui/icons-material/Nature';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import { supabase } from '../../services/supabaseClient'; // Adjust path if needed
import type { Worker } from '../../types/worker';

// Import farm components (we'll create these next)
import FarmOverview from '../../components/farm/details/FarmOverview';
import FarmCrops from '../../components/farm/details/FarmCrops';
import FarmAnimals from '../../components/farm/details/FarmAnimals';
import FarmInventory from '../../components/farm/details/FarmInventory';
import FarmWorkers from '../../components/farm/details/FarmWorkers';
import FarmFinances from '../../components/farm/details/FarmFinances';
import FarmTasks from '../../components/farm/details/FarmTasks';
import FarmAnalytics from '../../components/farm/details/FarmAnalytics';
import FarmSettings from '../../components/farm/details/FarmSettings';
import FarmWeather from '../../components/farm/details/FarmWeather';
import FarmSmartFarming from '../../components/farm/details/FarmSmartFarming';
import FarmProduce from '../../components/farm/details/FarmProduce';
import WorkerManagement from '../../components/workers/WorkerManagement';

const getTutorialMode = () => {
  const stored = localStorage.getItem('tutorialMode');
  return stored ? JSON.parse(stored) : false;
};

const mockWorkers: Worker[] = [
    {
      id: '1',
      name: 'John Doe',
    role: 'manager',
    phoneNumber: '',
    email: '',
    startDate: '',
    wage: 0,
    paymentType: 'monthly',
    status: 'active',
      specialization: ['Crop Management', 'Animal Care'],
    workingHours: { start: '', end: '' },
    leaveDays: 0,
    leaveHistory: [],
    emergencyContact: { name: '', relationship: '', phone: '' },
    notes: '',
    permissions: [],
    attendance: [],
    },
  // ...add more mock workers as needed
];

const FarmTasksWrapper = ({ farmId }: { farmId?: string }) => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tutorialMode = getTutorialMode();
    if (tutorialMode) {
      setWorkers(mockWorkers);
      setLoading(false);
      return;
    }
    if (!farmId) return;
    setLoading(true);
    supabase
      .from('workers')
      .select('*')
      .eq('farm_id', farmId)
      .then(({ data }) => {
        setWorkers(data || []);
        setLoading(false);
      });
  }, [farmId]);

  if (loading) return <div>Loading workers...</div>;

  return <FarmTasks farmId={farmId || 'default'} workers={workers} />;
};

const drawerWidth = 240;

const menuItems = [
  { path: '', label: 'Overview', icon: <DashboardIcon /> },
  { path: 'crops', label: 'Crops Management', icon: <CropsIcon /> },
  { path: 'animals', label: 'Livestock Management', icon: <AnimalsIcon /> },
  { path: 'inventory', label: 'Inventory', icon: <InventoryIcon /> },
  { path: 'produce', label: 'Produce', icon: <AgricultureIcon /> },
  { path: 'workers', label: 'Workers', icon: <WorkersIcon /> },
  { path: 'workers-management', label: 'Workers Management', icon: <WorkersIcon /> },
  { path: 'finances', label: 'Finances', icon: <FinanceIcon /> },
  { path: 'tasks', label: 'Tasks', icon: <TasksIcon /> },
  { path: 'analytics', label: 'Analytics', icon: <AnalyticsIcon /> },
  { path: 'weather', label: 'Weather', icon: <WeatherIcon /> },
  { path: 'settings', label: 'Settings', icon: <SettingsIcon /> },
];

// Create a context for the current farm
export const FarmContext = createContext<any>(null);

const FarmDetails: React.FC = () => {
  const { farmId = 'default-farm' } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [farm, setFarm] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tutorial mode check
  const tutorialMode = getTutorialMode();

  // Helper to refresh farm after settings update
  const refreshFarm = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from('farms')
      .select('*')
      .eq('id', farmId)
      .single();
    if (error || !data) {
      setError('Farm not found.');
      setFarm(null);
    } else {
      setFarm({ ...data, imageUrl: data.image_url });
    }
    setLoading(false);
  };

  useEffect(() => {
    let ignore = false;
    const fetchFarm = async () => {
      setLoading(true);
      setError(null);
      if (tutorialMode) {
        setFarm({
          id: 'mock-id',
          name: 'Green Valley Farm',
          location: 'Nakuru County',
          imageUrl: '',
          size: 10,
          status: 'active',
          description: '',
          crops: [],
        });
        setLoading(false);
        return;
      }
      const { data, error } = await supabase
        .from('farms')
        .select('*')
        .eq('id', farmId)
        .single();
      if (!ignore) {
        if (error || !data) {
          setError('Farm not found.');
          setFarm(null);
        } else {
          setFarm({ ...data, imageUrl: data.image_url });
        }
        setLoading(false);
      }
    };
    fetchFarm();
    return () => { ignore = true; };
  }, [farmId, tutorialMode]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Farm Management
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.path}
            onClick={() => {
              navigate(`/dashboard/my-farms/${farmId}/${item.path}`);
              if (isMobile) {
                setMobileOpen(false);
              }
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
    </div>
  );

  if (loading) return <Box sx={{ p: 4 }}>Loading farm details...</Box>;
  if (error) return <Box sx={{ p: 4, color: 'error.main' }}>{error}</Box>;
  if (!farm) return <Box sx={{ p: 4 }}>No farm data available.</Box>;

  return (
    <FarmContext.Provider value={{ farm, refreshFarm }}>
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ color: 'white' }}
          >
            <Link
              to="/dashboard"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Dashboard
            </Link>
            <Link
              to="/dashboard/my-farms"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              My Farms
            </Link>
            <Typography color="inherit">Farm Details</Typography>
          </Breadcrumbs>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          minHeight: '100vh',
          px: { xs: 1, sm: 2, md: 3 },
          maxWidth: 1400,
          mx: 'auto',
        }}
      >
        {/*
          NOTE: For wide tables in subpages, wrap them like this:
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Table>...</Table>
          </Box>
        */}
        {/* Optional: Show a banner if in tutorial mode */}
        {tutorialMode && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" color="info.main">
              Tutorial Mode: You are viewing mock data. Switch to Real Data Mode for your actual farm data.
            </Typography>
          </Box>
        )}
        <Box sx={{ width: '100%' }}>
          <Routes>
            <Route path="" element={<FarmOverview farm={farm} />} />
            <Route path="crops" element={<FarmCrops farm={farm} />} />
            <Route path="animals" element={<FarmAnimals farm={farm} />} />
            <Route path="inventory" element={<FarmInventory farm={farm} />} />
            <Route path="produce" element={<FarmProduce farm={farm} />} />
            <Route path="workers" element={<FarmWorkers farm={farm} />} />
            <Route path="workers-management" element={<WorkerManagement farmId={farm.id} />} />
            <Route path="finances" element={<FarmFinances farm={farm} />} />
            <Route path="tasks" element={<FarmTasksWrapper farmId={farm.id} />} />
            <Route path="smart-farming" element={<FarmSmartFarming farm={farm} />} />
            <Route path="analytics" element={<FarmAnalytics farm={farm} />} />
            <Route path="weather" element={<FarmWeather farm={farm} />} />
            <Route path="settings" element={<FarmSettings farm={farm} />} />
          </Routes>
        </Box>
      </Box>
    </Box>
    </FarmContext.Provider>
  );
};

export default FarmDetails;
